import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import PropTypes from "prop-types";
import SectionButtonGroup from "../../buttons/SectionButtonGroup";
import OpportunityAccordion from "./OpportunityAccordion";
import apiService from "../../../services/apiService";
import { useToast } from "../../../hooks/useToast";
import {
  matchingFields,
  updateMatchingFields,
} from "../../../utils/matchingFields";
import { useInteractiveTooltips } from "../../../hooks/useInteractiveTooltips";
import { validateFormAgainstRules } from "../../../utils/formValidation";

const importAllScopeFiles = () => {
  const scopeFiles = [
    "distributionSystemSow.json",
    "driftEliminatorsCFSow.json",
    "driftEliminatorsXFSow.json",
    "driveShaftSow.json",
    "fanSow.json",
    "fandeckSow.json",
    "fanstackSow.json",
    "fillSow.json",
    "firewallSow.json",
    "gearSow.json",
    "laddersSow.json",
    "mechanicalAccessSow.json",
    "mngSupportSow.json",
    "motorSow.json",
    "partitionwallSow.json",
    "stairwaySow.json",
    "structureSow.json",
    "windwallSow.json",
  ];

  const scopesContext = require.context(
    "../../../data/scopes",
    true,
    /\.json$/
  );

  return scopeFiles.reduce((acc, filename) => {
    const scopeName = filename.replace(/\.json$/, "");
    try {
      const scope = scopesContext(`./${filename}`);
      acc[scopeName] = scope;
    } catch (error) {
      console.error(`Failed to load scope ${filename}:`, error);
    }
    return acc;
  }, {});
};

const importAllComponents = () => {
  const specificFiles = [
    "airSealWall.json",
    "casing.json",
    "coldWaterBasin.json",
    "distributionSystem.json",
    "driftEliminators.json",
    "ladders.json",
    "fandeck.json",
    "fanstack.json",
    "fill.json",
    "firewall.json",
    "hotWaterBasin.json",
    "louvers.json",
    "mechanical.json",
    "partitionwall.json",
    "plenumAccessLadder.json",
    "stairway.json",
    "structure.json",
    "walkways.json",
    "windwall.json",
  ];

  const componentsContext = require.context("../../../data", true, /\.json$/);

  return specificFiles.reduce((acc, filename) => {
    const componentName = filename.replace(/\.json$/, "");
    const component = componentsContext(`./${filename}`);
    acc[componentName] = component;
    return acc;
  }, {});
};

const findValueByQuestionId = (
  formattedTowerData,
  questionId,
  selectedComponents,
  activeSection
) => {
  // First check if the value exists directly in the root object
  if (formattedTowerData[questionId] !== undefined) {
    return formattedTowerData[questionId];
  }

  if (activeSection === "scopeOfWork") {
    return (
      formattedTowerData.scopeOfWork?.[selectedComponents[0]]?.[questionId] ||
      undefined
    );
  }
  //is it in components?
  if (
    selectedComponents &&
    selectedComponents.length > 0 &&
    formattedTowerData.components?.[selectedComponents[0]]?.[questionId] !==
      undefined
  ) {
    return formattedTowerData.components[selectedComponents[0]][questionId];
  } // Check for special partition wall and firewall fields
  const specialFields = [
    "partitionwall_type",
    "partitionwall_material",
    "partitionwall_frp_type",
    "partitionwall_wood_type",
    "partitionwall_quantity",
    "partitionwall_between_cells",
    "partitionwall_panels_both_sides",
    "firewall_exists",
    "firewall_material",
    "firewall_frp_thickness",
    "firewall_cement_board_thickness",
    "firewall_between_cells",
    "firewall_quantity",
  ];

  if (
    specialFields.includes(questionId) &&
    formattedTowerData.generalTowerDescription
  ) {
    console.log(
      `Found special field: ${questionId} = ${formattedTowerData.generalTowerDescription[questionId]}`
    );
    return formattedTowerData.generalTowerDescription[questionId];
  }

  //is it in scope?
  if (
    formattedTowerData.scopeOfWork?.[selectedComponents[0]]?.[questionId] !==
    undefined
  ) {
    return formattedTowerData.scopeOfWork[selectedComponents[0]][questionId];
  }

  // Check in the fill object
  if (
    formattedTowerData.fill &&
    formattedTowerData.fill[questionId] !== undefined
  ) {
    return formattedTowerData.fill[questionId];
  }

  // Recursively search through nested objects
  for (const key in formattedTowerData) {
    if (
      typeof formattedTowerData[key] === "object" &&
      formattedTowerData[key] !== null
    ) {
      const value = findValueByQuestionId(
        formattedTowerData[key],
        questionId,
        selectedComponents,
        activeSection
      );
      if (value !== undefined) {
        return value;
      }
    }
  }

  return undefined;
};

const OpportunityModalBody = ({
  modalState,
  handleTabClick,
  formattedTowerData,
  setformattedTowerData,
  modalLoading,
  towerId,
  project,
  renderField,
}) => {
  const [activeSection, setActiveSection] = useState("description");
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [allComponents, setAllComponents] = useState({});
  const { addToast } = useToast();
  const [allScopes, setAllScopes] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  useInteractiveTooltips(
    {
      placement: "top",
      html: true,
      sanitize: false,
    },
    [formattedTowerData, modalState.selectedComponents, activeSection]
  );

  const getReferenceValue = (reference) => {
    if (!reference || !allComponents) return reference?.fallback;

    const { componentId, subComponentId, questionId, fallback } = reference;

    // First check if value exists in formattedTowerData
    if (subComponentId) {
      const value =
        formattedTowerData?.components?.[componentId]?.[questionId] ||
        formattedTowerData?.components?.[subComponentId]?.[questionId];
      if (value !== undefined) return value;
    }

    // If not found in formattedTowerData, check allComponents
    const component = allComponents[componentId];
    if (!component) return fallback;

    if (subComponentId) {
      const subComponent = component.subComponents?.find(
        (sub) => sub.id === subComponentId
      );
      if (!subComponent) return fallback;

      // Find the question in the subcomponent
      const question = subComponent.questions?.find((q) => q.id === questionId);
      const value =
        question?.value ||
        formattedTowerData?.components?.[subComponentId]?.[questionId];
      return value || fallback;
    }

    // Handle non-subcomponent references
    const question = component.questions?.find((q) => q.id === questionId);
    return (
      question?.value ||
      formattedTowerData?.components?.[componentId]?.[questionId] ||
      fallback
    );
  };

  useEffect(() => {
    const scopes = importAllScopeFiles();
    setAllScopes(scopes);
  }, []);

  const currentTowerType =
    formattedTowerData?.towerInfo?.towerType?.toLowerCase() ||
    formattedTowerData?.towerType?.toLowerCase() ||
    formattedTowerData?.generalTowerDescription?.tower?.type?.toLowerCase();

  useEffect(() => {
    setSelectedComponents([]);
    modalState.selectedComponents = [];
  }, []);

  useEffect(() => {
    const components = importAllComponents();
    setAllComponents(components);
  }, []);

  useEffect(() => {
    handleSectionClick("description");
  }, []);

  useEffect(() => {
    if (
      selectedComponents &&
      selectedComponents[0] &&
      (allComponents || allScopes)
    ) {
      const componentData =
        (allComponents && allComponents[selectedComponents[0]]) ||
        (allScopes && allScopes[selectedComponents[0]]);

      if (componentData && componentData.validationRules) {
        // Get the relevant form values for this component
        let componentValues;

        if (
          activeSection === "scopeOfWork" &&
          formattedTowerData &&
          formattedTowerData.scopeOfWork
        ) {
          componentValues =
            formattedTowerData.scopeOfWork[selectedComponents[0]];
        } else if (formattedTowerData && formattedTowerData.components) {
          componentValues =
            formattedTowerData.components[selectedComponents[0]];
        }

        // Only validate if we have component values
        if (componentValues) {
          try {
            // Validate against rules
            const validationErrors = validateFormAgainstRules(
              componentValues,
              componentData.validationRules
            );
            setFieldErrors(validationErrors);
          } catch (error) {
            console.error("Validation error:", error);
            // Clear errors on validation failure to prevent UI issues
            setFieldErrors({});
          }
        } else {
          // Clear any existing errors if we don't have component values
          setFieldErrors({});
        }
      } else {
        // Clear errors if no validation rules
        setFieldErrors({});
      }
    } else {
      // Clear errors if no selected component
      setFieldErrors({});
    }
  }, [
    selectedComponents,
    formattedTowerData,
    allComponents,
    allScopes,
    activeSection,
  ]);

  const sections = [
    { id: "description", label: "General Tower Description" },
    ...(towerId ? [{ id: "components", label: "Tower Components" }] : []),
    ...(towerId ? [{ id: "scopeOfWork", label: "Scope of Work" }] : []),
  ];

  const handleAnswerChange = async (questionId, value) => {
    // Clear any errors for this field
    if (fieldErrors[questionId]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }

    let updatedTowerData = { ...formattedTowerData };

    if (activeSection === "scopeOfWork") {
      if (selectedComponents[0] && questionId) {
        const fieldPath = `scopeOfWork.${selectedComponents[0]}.${questionId}`;
        updateMatchingFields(
          updatedTowerData,
          fieldPath,
          value,
          matchingFields
        );
      }
      // Scope of Work saving logic - this is correct
      updatedTowerData.scopeOfWork = {
        ...(updatedTowerData.scopeOfWork || {}),
        [selectedComponents[0]]: {
          ...(updatedTowerData.scopeOfWork?.[selectedComponents[0]] || {}),
          [questionId]: value || null,
        },
      };
    } else {
      // Components saving logic needs adjustment

      if (selectedComponents[0] && questionId) {
        const fieldPath = `components.${selectedComponents[0]}.${questionId}`;
        updateMatchingFields(
          updatedTowerData,
          fieldPath,
          value,
          matchingFields
        );
      }

      switch (selectedComponents[0]) {
        case "distributionSystem":
          // Store all distribution system data under one key
          updatedTowerData.components.distributionSystem = {
            ...(updatedTowerData.components?.distributionSystem || {}),
            [questionId]: value || null,
          };
          break;

        case "fill":
        case "louvers":
        case "mechanical":
        case "fan":
        case "fanstack":
        case "stairway":
        case "ladders":
        case "structure":
        case "coldWaterBasin":
        case "walkways":
        case "windwall":
        case "partitionwall":
        case "firewall":
          // Update components with proper path
          updatedTowerData.components = {
            ...(updatedTowerData.components || {}),
            [selectedComponents[0]]: {
              ...(updatedTowerData.components?.[selectedComponents[0]] || {}),
              [questionId]: value || null,
            },
          };

          // Also update general description if needed
          if (
            questionId.includes("_exists") ||
            questionId.includes("_type") ||
            questionId.includes("_count") ||
            questionId.includes("_quantity") ||
            questionId === "partitionwall_between_cells" ||
            questionId === "partitionwall_panels_both_sides" ||
            questionId === "partitionwall_frp_type" ||
            questionId === "partitionwall_wood_type" ||
            questionId === "firewall_material" ||
            questionId === "firewall_frp_thickness" ||
            questionId === "firewall_cement_board_thickness" ||
            questionId === "firewall_between_cells" ||
            questionId === "firewall_quantity"
          ) {
            // Log firewall field updates
            if (questionId.includes("firewall_")) {
              console.log(
                `Updating firewall field in generalTowerDescription: ${questionId} = ${value}`
              );
            }

            updatedTowerData.generalTowerDescription = {
              ...(updatedTowerData.generalTowerDescription || {}),
              [questionId]: value,
            };
          }
          break;

        case "tower":
          // Update tower configuration
          updatedTowerData.generalTowerDescription = {
            ...(updatedTowerData.generalTowerDescription || {}),
            tower: {
              ...(updatedTowerData.generalTowerDescription?.tower || {}),
              [questionId]: value || null,
            },
          };
          break;

        default:
          // Handle any other components
          updatedTowerData.components = {
            ...(updatedTowerData.components || {}),
            [selectedComponents[0]]: {
              ...(updatedTowerData.components?.[selectedComponents[0]] || {}),
              [questionId]: value || null,
            },
          };
          break;
      }
    }

    // Update state
    setformattedTowerData(updatedTowerData);

    console.log("Updated Tower Data:", updatedTowerData);

    // Save to database
    try {
      await apiService.post(`/tower/updateTowerBySerial`, {
        towerInfo: updatedTowerData,
      });
      addToast("Entry Updated Successfully");
    } catch (error) {
      console.error("Error saving tower data:", error);
      addToast("Error Saving Update.", "danger");
    }
  };

  const handleComponentSelect = (component, level) => {
    setSelectedComponents((prev) => {
      const newSelected = [...prev.slice(0, level)];
      newSelected[level] = component;
      return newSelected;
    });
    modalState.selectedComponents = [component];
  };

  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    handleTabClick(sectionId);
  };
  const renderTowerInfo = (data) => {
    console.log("Rendering tower info with data:", data);
    console.log("Partition wall data:", {
      between_cells: data.generalTowerDescription?.partitionwall_between_cells,
      panels_both_sides:
        data.generalTowerDescription?.partitionwall_panels_both_sides,
      from_components:
        data.components?.partitionwall?.partitionwall_between_cells,
      components_both_sides:
        data.components?.partitionwall?.partitionwall_panels_both_sides,
    });

    // Add debug logging for Fire Wall data
    console.log("Fire Wall data:", {
      exists: data.generalTowerDescription?.firewall_exists,
      material: data.generalTowerDescription?.firewall_material,
      frp_thickness: data.generalTowerDescription?.firewall_frp_thickness,
      cement_board_thickness:
        data.generalTowerDescription?.firewall_cement_board_thickness,
      between_cells: data.generalTowerDescription?.firewall_between_cells,
      quantity: data.generalTowerDescription?.firewall_quantity,
      from_components: data.components?.firewall?.firewall_exists,
    });

    const sections = {
      general: {
        title: "General Information",
        fields: [
          { name: "Manufacturer", key: "generalTowerDescription.manufacturer" },
          { name: "Model", key: "generalTowerDescription.model" },
          {
            name: "Customer Tower Designation",
            key: "generalTowerDescription.customer_tower_designation",
          },
          {
            name: "Serial Number",
            key: "generalTowerDescription.serialNumber",
          },
          { name: "Submitter", key: "generalTowerDescription.submitter" },
        ],
      },
      location: {
        title: "Location",
        fields: [
          { name: "City", key: "generalTowerDescription.city" },
          { name: "State", key: "generalTowerDescription.state" },
          { name: "Street", key: "generalTowerDescription.street" },
          { name: "Zip", key: "generalTowerDescription.zip" },
        ],
      },
      tower: {
        title: "Tower Configuration",
        fields: [
          { name: "Tower Type", key: "generalTowerDescription.towerType" },
          {
            name: "Tower Subtype",
            key: "generalTowerDescription.towerSubtype",
          },
          {
            name: "Distribution System",
            key: "components.distributionSystem.dist_cell_arrangement_cf",
          },
        ],
      },
      dimensions: {
        title: "Dimensions",
        fields: [
          {
            name: "Number of Cells",
            key: "generalTowerDescription.numberOfCells",
          },
          { name: "Cell Length", key: "generalTowerDescription.cellLength" },
          { name: "Cell Width", key: "generalTowerDescription.cellWidth" },
          { name: "Cell Height", key: "generalTowerDescription.cellHeight" },
          {
            name: "Bay Size Length",
            key: "generalTowerDescription.baySizeLength",
          },
          {
            name: "Bay Size Width",
            key: "generalTowerDescription.baySizeWidth",
          },
          {
            name: "Bay Size Height",
            key: "generalTowerDescription.baySizeHeight",
          },
          {
            name: "Curb Height From Grade",
            key: "components.coldWaterBasin.cwb_grade_to_curb_height",
          },
        ],
      },
      louvers: {
        title: "Louvers",
        fields: [
          { name: "Exists", key: "generalTowerDescription.louver_exists" },
          {
            name: "Louver Row Count",
            key: "generalTowerDescription.louver_row_count",
          },
          {
            name: "Louver Blade Width",
            key: "generalTowerDescription.louver_blade_width",
          },
          {
            name: "Louver Material",
            key: "generalTowerDescription.louver_material",
          },
          {
            name: "Air Inlet Height",
            key: "generalTowerDescription.airInletHeight",
          },
          {
            name: "Air Inlets on End Walls",
            key: "components.casing.casing_air_inlets",
          },
          {
            name: "Casing Material",
            key:
              currentTowerType === "counterflow"
                ? "components.casing.casing_material_cf"
                : "components.casing.casing_material_xf",
          },
        ],
      },
      fill: {
        title: "Fill",
        fields: [
          {
            name: `Fill Type (${
              currentTowerType?.charAt(0).toUpperCase() +
              currentTowerType?.slice(1)
            })`,
            key: "generalTowerDescription.fill_type",
          },
          ...(currentTowerType === "crossflow"
            ? [
                {
                  name: "Orientation to Air Flow",
                  key: "components.fill.fill_orientation_xf",
                },
              ]
            : []),
        ],
      },
      mechanical: {
        title: "Mechanical",
        fields: [
          { name: "Motor HP", key: "components.mechanical.motor_hp" },
          {
            name: "Fan Diameter",
            key: "generalTowerDescription.fan_diameter",
          },
          {
            name: "Fanstack Height",
            key: "generalTowerDescription.fanstack_height",
          },
        ],
      },
      distribution: {
        title: "Distribution",
        fields: [
          {
            name: "Distribution Header CL From Top Of CWB Curb",
            key: "generalTowerDescription.dist_header_cl_cwb_curb",
          },
        ],
      },
      structure: {
        title: "Structure",
        fields: [
          {
            name: "Structure Material",
            key: "generalTowerDescription.structure_material",
          },
          {
            name: "Number of Ladders",
            key: "generalTowerDescription.ladder_count",
          },
          {
            name: "Number of Stairs",
            key: "generalTowerDescription.stairway_count",
          },
        ],
      },
      partitionwall: {
        title: "Partition Wall",
        fields: [
          {
            name: "Partition Wall Type",
            key: "generalTowerDescription.partitionwall_type",
          },
          {
            name: "Partition Wall Material",
            key: "generalTowerDescription.partitionwall_material",
          },
          {
            name: "Partition Wall FRP Type",
            key: "generalTowerDescription.partitionwall_frp_type",
          },
          {
            name: "Partition Wall Wood Type",
            key: "generalTowerDescription.partitionwall_wood_type",
          },
          {
            name: "Quantity of Partition Walls",
            key: "generalTowerDescription.partitionwall_quantity",
          },
          {
            name: "Is there wall between each cell?",
            key: "generalTowerDescription.partitionwall_between_cells",
          },
          {
            name: "Are panels Installed on Both sides of Column?",
            key: "generalTowerDescription.partitionwall_panels_both_sides",
          },
        ],
      },
      firewall: {
        title: "Fire Wall",
        fields: [
          {
            name: "Fire Wall Exists",
            key: "generalTowerDescription.firewall_exists",
          },
          {
            name: "Fire Wall Material",
            key: "generalTowerDescription.firewall_material",
          },
          {
            name: "Fire Wall FRP Thickness",
            key: "generalTowerDescription.firewall_frp_thickness",
          },
          {
            name: "Fire Wall Cement Board Thickness",
            key: "generalTowerDescription.firewall_cement_board_thickness",
          },
          {
            name: "Is there a fire wall between each cell?",
            key: "generalTowerDescription.firewall_between_cells",
          },
          {
            name: "Quantity of Fire Walls",
            key: "generalTowerDescription.firewall_quantity",
          },
        ],
      },
      notes: {
        title: "Additional Information",
        fields: [
          { name: "Tower Notes", key: "generalTowerDescription.tower_notes" },
        ],
      },
    };

    return (
      <div className="tower-info p-3">
        {Object.entries(sections).map(([sectionName, sectionData]) => {
          const hasValues = sectionData.fields.some((fieldData) => {
            let fieldValue;

            // Special handling for partition wall fields
            if (fieldData.key.includes("partitionwall_")) {
              const fieldName = fieldData.key.split(".")[1];
              fieldValue =
                data.generalTowerDescription?.[fieldName] ||
                data.components?.partitionwall?.[fieldName];
              return fieldValue != null;
            }

            // Special handling for firewall fields
            if (fieldData.key.includes("firewall_")) {
              const fieldName = fieldData.key.split(".")[1];
              fieldValue =
                data.generalTowerDescription?.[fieldName] ||
                data.components?.firewall?.[fieldName];
              return fieldValue != null;
            }

            const t_value = fieldData.key
              .split(".")
              .reduce((obj, part) => obj?.[part], data);

            return t_value != null;
          });

          // Add debugging for partition wall section
          if (sectionName === "partitionwall") {
            console.log("Partition wall section data:", {
              sectionData,
              fields: sectionData.fields.map((f) => ({
                name: f.name,
                key: f.key,
                value: f.key.includes("partitionwall_")
                  ? data.generalTowerDescription?.[f.key.split(".")[1]] ||
                    data.components?.partitionwall?.[f.key.split(".")[1]]
                  : f.key.split(".").reduce((obj, part) => obj?.[part], data),
              })),
            });
          }

          // Add debugging for firewall section
          if (sectionName === "firewall") {
            console.log("Fire Wall section data:", {
              sectionData,
              fields: sectionData.fields.map((f) => ({
                name: f.name,
                key: f.key,
                value: f.key.includes("firewall_")
                  ? data.generalTowerDescription?.[f.key.split(".")[1]] ||
                    data.components?.firewall?.[f.key.split(".")[1]]
                  : f.key.split(".").reduce((obj, part) => obj?.[part], data),
              })),
            });
          }

          return (
            <div key={sectionName} className="mb-4">
              <h6
                className="border-bottom pb-2 mb-3"
                style={{ fontSize: "0.9rem" }}
              >
                {sectionData.title}
              </h6>
              <div className="row">
                {sectionData.fields.map((fieldData) => {
                  let value;

                  // Special handling for partition wall fields
                  if (fieldData.key.includes("partitionwall_")) {
                    const fieldName = fieldData.key.split(".")[1];
                    value =
                      data.generalTowerDescription?.[fieldName] ||
                      data.components?.partitionwall?.[fieldName];
                    console.log(`Partition wall field ${fieldName}: `, value);
                  }
                  // Special handling for firewall fields
                  else if (fieldData.key.includes("firewall_")) {
                    const fieldName = fieldData.key.split(".")[1];
                    value =
                      data.generalTowerDescription?.[fieldName] ||
                      data.components?.firewall?.[fieldName];
                    console.log(`Fire wall field ${fieldName}: `, value);
                  } else {
                    value = fieldData.key
                      .split(".")
                      .reduce((obj, part) => obj?.[part], data);
                  }

                  if (value == null || value === "") return null;

                  let displayValue = value;
                  if (typeof value === "object") {
                    displayValue = JSON.stringify(value);
                  } else if (typeof value === "string") {
                    displayValue = value
                      .split(/[_ ]+/) // Split by underscores or spaces
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ");
                  }

                  return (
                    <div key={fieldData.key} className="col-md-6 mb-2">
                      <div
                        className="d-flex align-items-center"
                        style={{ fontSize: "0.85rem" }}
                      >
                        <strong className="me-2">{fieldData.name}:</strong>
                        <span className="text-primary">{displayValue}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderFormField = (question) => {
    if (!question || !question.type) return null;

    if (question.dependsOn && question.showWhen) {
      const parentValue =
        formattedTowerData[activeSection]?.[selectedComponents[0]]?.[
          question.dependsOn
        ];
      if (parentValue !== question.showWhen) {
        return null;
      }
    }

    if (question.towerSubtype) {
      const currentTowerSubtype =
        formattedTowerData?.generalTowerDescription?.towerSubtype ||
        formattedTowerData?.towerInfo?.towerSubtype;

      console.log("Question tower subtype:", question.towerSubtype);
      console.log("Current tower subtype:", currentTowerSubtype);

      // Normalize both values to lowercase and replace spaces with underscores
      const normalizedQuestionSubtype =
        typeof question.towerSubtype === "string"
          ? question.towerSubtype.toLowerCase().replace(/\s+/g, "_")
          : String(question.towerSubtype).toLowerCase().replace(/\s+/g, "_");

      const normalizedCurrentSubtype = currentTowerSubtype
        ? currentTowerSubtype.toLowerCase().replace(/\s+/g, "_")
        : "";

      if (normalizedQuestionSubtype !== normalizedCurrentSubtype) {
        return null;
      }
    }

    if (question.towerType) {
      const currentTowerType =
        formattedTowerData?.generalTowerDescription?.tower?.type?.toLowerCase() ||
        formattedTowerData?.towerType?.toLowerCase();

      if (
        typeof question.towerType === "string" &&
        question.towerType !== currentTowerType &&
        question.towerType !== "both"
      ) {
        return null;
      }

      if (
        Array.isArray(question.towerType) &&
        !question.towerType.includes(currentTowerType)
      ) {
        return null;
      }
    }

    let value;
    // if (question.reference) {
    //   value = getReferenceValue(question.reference);
    // } else {
    value =
      findValueByQuestionId(
        formattedTowerData,
        question.id,
        selectedComponents,
        activeSection
      ) || question.defaultValue;
    // }

    const hasError = fieldErrors[question.id];

    switch (question.type) {
      case "section":
        if (question.dependsOn) {
          const towerSubtype =
            formattedTowerData?.generalTowerDescription?.tower?.subtype
              ?.toLowerCase()
              ?.replace(/\s+/g, "_");

          if (!question.showForValues?.includes(towerSubtype)) {
            return null;
          }
        }
        return (
          <div className="section-group">
            <div className="ms-3">
              {question.questions?.map((q) => {
                const selectedOption = q.options?.find(
                  (opt) => opt.value === formattedTowerData[q.id]
                );

                return (
                  <div key={q.id} className="mb-3">
                    {q.type !== "section" && (
                      <label className="form-label">
                        {q.text}
                        {q.required && <span className="text-danger">*</span>}
                      </label>
                    )}
                    <div>
                      {renderFormField(q)}
                      {selectedOption?.subQuestions && (
                        <div className="ms-4 mt-2">
                          {Array.isArray(selectedOption.subQuestions) ? (
                            selectedOption.subQuestions.map((subQ) => (
                              <div key={subQ.id} className="form-group mb-3">
                                <label className="form-label">
                                  {subQ.text}
                                  {subQ.required && (
                                    <span className="text-danger">*</span>
                                  )}
                                </label>
                                {renderFormField(subQ)}
                              </div>
                            ))
                          ) : (
                            <div className="form-group mb-3">
                              <label className="form-label">
                                {selectedOption.subQuestions.text}
                                {selectedOption.subQuestions.required && (
                                  <span className="text-danger">*</span>
                                )}
                              </label>
                              {renderFormField(selectedOption.subQuestions)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "checkbox":
        // Find which options are currently selected
        const selectedOptions = question.options?.filter((option) =>
          Array.isArray(value)
            ? value.includes(option.value)
            : value === option.value
        );

        return (
          <>
            <div className="checkbox-group">
              {question.options?.map((option) => {
                const isChecked = Array.isArray(value)
                  ? value.includes(option.value)
                  : value === option.value;

                return (
                  <div className="form-check mb-2" key={option.value}>
                    <input
                      type="checkbox"
                      className={`form-check-input ${
                        hasError ? "is-invalid" : ""
                      }`}
                      id={`${question.id}-${option.value}`}
                      value={option.value}
                      checked={isChecked}
                      onChange={(e) => {
                        // If it's a single checkbox
                        if (!question.multiple) {
                          handleAnswerChange(
                            question.id,
                            e.target.checked ? option.value : null
                          );
                          return;
                        }

                        // For multiple checkboxes, manage an array of values
                        const currentValues = Array.isArray(value)
                          ? [...value]
                          : [];
                        if (e.target.checked) {
                          // Add value if checked
                          if (!currentValues.includes(option.value)) {
                            handleAnswerChange(question.id, [
                              ...currentValues,
                              option.value,
                            ]);
                          }
                        } else {
                          // Remove value if unchecked
                          handleAnswerChange(
                            question.id,
                            currentValues.filter((val) => val !== option.value)
                          );
                        }
                      }}
                      required={question.required}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`${question.id}-${option.value}`}
                    >
                      {option.label}
                    </label>
                  </div>
                );
              })}
            </div>

            {hasError && (
              <div className="invalid-feedback d-block">
                {fieldErrors[question.id]}
              </div>
            )}

            {/* Render question-level subQuestions if any */}
            {value && question.subQuestions && (
              <div className="ms-4 mt-2">
                {Array.isArray(question.subQuestions) ? (
                  question.subQuestions.map((subQ) => (
                    <div key={subQ.id} className="form-group mb-3">
                      <label className="form-label">
                        {subQ.text}
                        {subQ.required && (
                          <span className="text-danger">*</span>
                        )}
                      </label>
                      {renderFormField(subQ)}
                    </div>
                  ))
                ) : (
                  <div className="form-group mb-3">
                    <label className="form-label">
                      {question.subQuestions.text}
                      {question.subQuestions.required && (
                        <span className="text-danger">*</span>
                      )}
                    </label>
                    {renderFormField(question.subQuestions)}
                  </div>
                )}
              </div>
            )}

            {/* Render option-specific subQuestions for selected options */}
            {selectedOptions && selectedOptions.length > 0 && (
              <div className="ms-4 mt-2">
                {selectedOptions.map((option) =>
                  option.subQuestions ? (
                    <div
                      key={`subq-${option.value}`}
                      className="form-group mb-3"
                    >
                      {Array.isArray(option.subQuestions) ? (
                        option.subQuestions.map((subQ) => (
                          <div key={subQ.id}>
                            <label className="form-label">
                              {subQ.text}
                              {subQ.required && (
                                <span className="text-danger">*</span>
                              )}
                            </label>
                            {renderFormField(subQ)}
                          </div>
                        ))
                      ) : (
                        <>
                          <label className="form-label">
                            {option.subQuestions.text}
                            {option.subQuestions.required && (
                              <span className="text-danger">*</span>
                            )}
                          </label>
                          {renderFormField(option.subQuestions)}
                        </>
                      )}
                    </div>
                  ) : null
                )}
              </div>
            )}
          </>
        );

      case "dropdown":
        const selectedOption = question.options?.find(
          (opt) => opt.value === value
        );
        return (
          <>
            <select
              className={`form-select ${hasError ? "is-invalid" : ""}`}
              value={value || ""}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              required={question.required}
            >
              <option value="">Select an option</option>
              {question.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {hasError && (
              <div className="invalid-feedback d-block">
                {fieldErrors[question.id]}
              </div>
            )}
            {value &&
              (question.subQuestions || selectedOption?.subQuestions) && (
                <div className="ms-4 mt-2">
                  {Array.isArray(
                    question.subQuestions || selectedOption?.subQuestions
                  ) ? (
                    (
                      question.subQuestions || selectedOption?.subQuestions
                    )?.map((subQ) => (
                      <div key={subQ.id} className="form-group mb-3">
                        <label className="form-label">
                          {subQ.text}
                          {subQ.required && (
                            <span className="text-danger">*</span>
                          )}
                        </label>
                        {renderFormField(subQ)}
                      </div>
                    ))
                  ) : (
                    <div className="form-group mb-3">
                      <label className="form-label">
                        {selectedOption.subQuestions.text}
                        {selectedOption.subQuestions.required && (
                          <span className="text-danger">*</span>
                        )}
                      </label>
                      {renderFormField(selectedOption.subQuestions)}
                    </div>
                  )}
                </div>
              )}
          </>
        );
      case "text":
      case "textarea":
        return (
          <>
            <div className="input-group">
              <input
                type="text"
                className={`form-control ${hasError ? "is-invalid" : ""}`}
                value={value}
                onChange={(e) =>
                  handleAnswerChange(question.id, e.target.value)
                }
                placeholder={question.placeholder || ""}
                required={question.required}
                {...(question.type === "textarea"
                  ? { as: "textarea", rows: 3 }
                  : {})}
              />
              {question.unit && (
                <span className="input-group-text">{question.unit}</span>
              )}
              {hasError && (
                <div className="invalid-feedback d-block">
                  {fieldErrors[question.id]}
                </div>
              )}
            </div>
          </>
        );
      case "number":
        const displayValue =
          value && !isNaN(value) && Number(value) >= 1000
            ? Number(value).toLocaleString()
            : value;

        return (
          <div className="input-group">
            <input
              type="text"
              inputMode="numeric"
              className={`form-control ${hasError ? "is-invalid" : ""}`}
              value={displayValue}
              onChange={(e) => {
                const rawValue = e.target.value.replace(/,/g, "");
                handleAnswerChange(question.id, rawValue);
                if (rawValue && !isNaN(rawValue) && Number(rawValue) >= 1000) {
                  e.target.value = Number(rawValue).toLocaleString();
                }
              }}
              placeholder={question.placeholder || ""}
              required={question.required}
            />
            {question.unit && (
              <span className="input-group-text">{question.unit}</span>
            )}
            {hasError && (
              <div className="invalid-feedback d-block">
                {fieldErrors[question.id]}
              </div>
            )}
          </div>
        );

      default:
        return (
          <>
            <input
              type="text"
              className={`form-control ${hasError ? "is-invalid" : ""}`}
              value={value}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              required={question.required}
            />
            {hasError && (
              <div className="invalid-feedback d-block">
                {fieldErrors[question.id]}
              </div>
            )}
          </>
        );
    }
  };

  const renderComponents = () => {
    if (!project) return null;

    const currentTowerType =
      formattedTowerData?.generalTowerDescription?.tower?.type?.toLowerCase() ||
      formattedTowerData?.towerType?.toLowerCase();

    return (
      <div className="component-columns">
        <div className="component-column">
          <div className="list-group fade-in">
            {Object.entries(allComponents).map(([key, component]) => {
              // Special handling for distribution system
              if (key === "distributionSystem") {
                // Show distribution system for all tower types
                return (
                  <button
                    key="distributionSystem"
                    className={`list-group-item list-group-item-action ${
                      modalState?.selectedComponents?.[0] === key
                        ? "active"
                        : ""
                    }`}
                    onClick={() => handleComponentSelect(key, 0)}
                  >
                    Distribution System
                  </button>
                );
              }

              // Regular component handling
              const shouldShowComponent =
                component.towerType === "both" ||
                component.towerType === currentTowerType ||
                (Array.isArray(component.towerType) &&
                  component.towerType.includes(currentTowerType)) ||
                !component.towerType;

              if (!shouldShowComponent) return null;

              return (
                <React.Fragment key={key}>
                  <button
                    className={`list-group-item list-group-item-action ${
                      modalState?.selectedComponents?.[0] === key &&
                      modalState?.selectedComponents?.length == 1
                        ? "active"
                        : ""
                    }`}
                    onClick={() => {
                      if (
                        modalState?.selectedComponents?.[0] === key &&
                        component.subComponents
                      ) {
                        setSelectedComponents([]);
                        modalState.selectedComponents = [];
                      } else {
                        handleComponentSelect(key, 0);
                      }
                    }}
                  >
                    {component.name || key}
                    {component.subComponents && (
                      <i className="bi bi-chevron-down ms-2 float-end"></i>
                    )}
                  </button>

                  {(selectedComponents?.[0] === key ||
                    component.subid === key) &&
                    component.subComponents && (
                      <div className="ms-3">
                        {component.subComponents.map((subComponent) => {
                          return (
                            <button
                              key={subComponent.id}
                              className={`list-group-item list-group-item-action ${
                                selectedComponents?.[1] === subComponent.id
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() =>
                                handleComponentSelect(subComponent.id, 1)
                              }
                            >
                              {subComponent.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="component-column">
          {modalState?.selectedComponents?.[0] && (
            <div className="questions-container">
              {selectedComponents[1]
                ? allComponents[selectedComponents[0]]?.subComponents
                    ?.find((sub) => sub.id === selectedComponents[1])
                    ?.questions?.map((question) => {
                      const field = renderFormField(question);
                      if (!field) return null;

                      return (
                        <div key={question.id} className="mb-3">
                          <label className="form-label">
                            {question.text}
                            {question.required && (
                              <span className="text-danger">*</span>
                            )}
                            {question.tooltip && (
                              <i
                                className="bi bi-info-circle ms-2"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                data-bs-html="true"
                                data-bs-title={question.tooltip}
                                title={question.tooltip}
                                style={{ cursor: "pointer" }}
                              />
                            )}
                          </label>
                          {field}
                        </div>
                      );
                    })
                : allComponents[selectedComponents[0]]?.questions?.map(
                    (question) => {
                      const field = renderFormField(question);
                      if (!field) return null;

                      return (
                        <div key={question.id} className="mb-3">
                          <label className="form-label">
                            {question.text}
                            {question.required && (
                              <span className="text-danger">*</span>
                            )}
                            {question.tooltip && (
                              <i
                                className="bi bi-info-circle ms-2"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                data-bs-html="true"
                                data-bs-title={question.tooltip}
                                title={question.tooltip}
                                style={{ cursor: "pointer" }}
                              />
                            )}
                          </label>
                          {field}
                        </div>
                      );
                    }
                  )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderScopeOfWork = () => {
    const currentTowerType =
      formattedTowerData?.generalTowerDescription?.tower?.type?.toLowerCase() ||
      formattedTowerData?.towerType?.toLowerCase();

    return (
      <div className="component-columns">
        <div className="component-column">
          <div className="list-group fade-in">
            {Object.entries(allScopes).map(([key, scope]) => {
              const shouldShowScope =
                scope.towerType === "both" ||
                (Array.isArray(scope.towerType) &&
                  scope.towerType.includes(currentTowerType));

              if (!shouldShowScope) return null;

              const includeInSow =
                (formattedTowerData.scopeOfWork?.[key]?.sow_include_in ||
                  scope.questions?.[0]?.defaultValue ||
                  "no") === "yes";

              const toggleScopeInclusion = async (key, event) => {
                event.stopPropagation();

                const currentValue =
                  formattedTowerData.scopeOfWork?.[key]?.sow_include_in || "no";
                const newValue = currentValue === "yes" ? "no" : "yes";

                const updatedTowerData = {
                  ...formattedTowerData,
                  scopeOfWork: {
                    ...(formattedTowerData.scopeOfWork || {}),
                    [key]: {
                      ...(formattedTowerData.scopeOfWork?.[key] || {}),
                      sow_include_in: newValue,
                    },
                  },
                };

                setformattedTowerData(updatedTowerData);

                try {
                  await apiService.post(`/tower/updateTowerBySerial`, {
                    towerInfo: updatedTowerData,
                  });
                  addToast("Scope inclusion updated successfully");
                } catch (error) {
                  console.error("Error updating scope inclusion:", error);
                  addToast("Error updating scope inclusion", "danger");
                }
              };

              return (
                <button
                  key={key}
                  className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                    modalState?.selectedComponents?.[0] === key ? "active" : ""
                  } ${includeInSow ? "included-sow" : "exclude-sow"}`}
                  onClick={() => handleComponentSelect(key, 0)}
                >
                  {scope.name}
                  <i
                    className={`bi ${
                      includeInSow ? "bi-check-square-fill" : "bi-square"
                    }`}
                    onClick={(e) => toggleScopeInclusion(key, e)}
                    style={{ cursor: "pointer" }}
                  ></i>
                </button>
              );
            })}
          </div>
        </div>

        <div className="component-column">
          {modalState?.selectedComponents?.[0] && (
            <div className="questions-container">
              {allScopes[modalState.selectedComponents[0]]?.questions
                ?.filter(Boolean)
                .map((question) => {
                  if (!question || !question.text) return null;

                  if (question.dependsOn) {
                    const parentValue =
                      formattedTowerData.scopeOfWork?.[selectedComponents[0]]?.[
                        question.dependsOn
                      ];
                    if (
                      !parentValue ||
                      (question.showWhen && parentValue !== question.showWhen)
                    ) {
                      return null;
                    }
                  }

                  const field = renderFormField(question);
                  if (!field) return null;

                  return (
                    <div key={question.id} className="form-group mb-3">
                      <label className="form-label">
                        {question.text}
                        {question.required && (
                          <span className="text-danger">*</span>
                        )}
                      </label>
                      {field}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Ensure partition wall fields are properly structured in the data
  useEffect(() => {
    if (formattedTowerData && formattedTowerData.components?.partitionwall) {
      // Check if the partition wall fields exist in components but not in generalTowerDescription
      const partitionWallFields = [
        "partitionwall_between_cells",
        "partitionwall_panels_both_sides",
      ];

      let needsUpdate = false;
      const updatedData = { ...formattedTowerData };

      if (!updatedData.generalTowerDescription) {
        updatedData.generalTowerDescription = {};
      }

      partitionWallFields.forEach((field) => {
        if (
          formattedTowerData.components.partitionwall[field] &&
          !formattedTowerData.generalTowerDescription[field]
        ) {
          updatedData.generalTowerDescription[field] =
            formattedTowerData.components.partitionwall[field];
          needsUpdate = true;
        }
      });

      if (needsUpdate) {
        setformattedTowerData(updatedData);
      }
    }
  }, [formattedTowerData]);

  // Ensure firewall fields are properly structured in the data
  useEffect(() => {
    if (formattedTowerData && formattedTowerData.components?.firewall) {
      // Check if the firewall fields exist in components but not in generalTowerDescription
      const firewallFields = [
        "firewall_exists",
        "firewall_material",
        "firewall_frp_thickness",
        "firewall_cement_board_thickness",
        "firewall_between_cells",
        "firewall_quantity",
      ];

      let needsUpdate = false;
      const updatedData = { ...formattedTowerData };

      if (!updatedData.generalTowerDescription) {
        updatedData.generalTowerDescription = {};
      }

      firewallFields.forEach((field) => {
        if (
          formattedTowerData.components.firewall[field] &&
          !formattedTowerData.generalTowerDescription[field]
        ) {
          updatedData.generalTowerDescription[field] =
            formattedTowerData.components.firewall[field];
          needsUpdate = true;
        }
      });

      if (needsUpdate) {
        setformattedTowerData(updatedData);
      }
    }
  }, [formattedTowerData]);

  return (
    <Modal.Body>
      <SectionButtonGroup
        sections={sections}
        activeSection={activeSection}
        onSectionClick={handleSectionClick}
      />

      <div id="accordionTower" className="mb-3">
        <div
          className={`collapse ${
            activeSection === "description" ? "show" : ""
          }`}
          id="collapseDescription"
          data-bs-parent="#accordionTower"
        >
          <div className="card">
            <div className="card-body">
              {!formattedTowerData ||
              Object.keys(formattedTowerData).length === 0 ? (
                <p>Tower Description Not Found</p>
              ) : (
                renderTowerInfo(formattedTowerData)
              )}
            </div>
          </div>
        </div>

        <div
          className={`collapse ${activeSection === "components" ? "show" : ""}`}
          id="collapseComponents"
          data-bs-parent="#accordionTower"
        >
          <div className="card">
            <div className="card-body compact-list">{renderComponents()}</div>
          </div>
        </div>

        <div
          className={`collapse ${
            activeSection === "scopeOfWork" ? "show" : ""
          }`}
          id="collapseScopeOfWork"
          data-bs-parent="#accordionTower"
        >
          <div className="card">
            <div className="card-body compact-list">{renderScopeOfWork()}</div>
          </div>
        </div>
      </div>

      <OpportunityAccordion project={project} renderField={renderField} />
    </Modal.Body>
  );
};

OpportunityModalBody.propTypes = {
  modalState: PropTypes.object.isRequired,
  handleTabClick: PropTypes.func.isRequired,
  formattedTowerData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  setformattedTowerData: PropTypes.func.isRequired,
  modalLoading: PropTypes.bool.isRequired,
  towerId: PropTypes.string.isRequired,
  project: PropTypes.object.isRequired,
  renderField: PropTypes.func.isRequired,
};

export default OpportunityModalBody;
